import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { handleSelectionChange } from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import {
  IAssetViewerStateContextValue,
  ShapesDrawingToolsProps
} from '@context/AssetViewerStateContext';
import type { PdfPinchGestureState } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/useMobilePdfPinchZoom';

function extractTextFromNode(node: Node): string {
  if (node.nodeName === 'BR') return '\n';
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
  if (node.nodeType === Node.ELEMENT_NODE)
    return Array.from(node.childNodes).map(extractTextFromNode).join('');
  return '';
}

type UseMobilePdfTextHighlightParams = {
  pageRefsMap: React.MutableRefObject<Map<number, HTMLDivElement>>;
  pageCanvasRef: RefObject<HTMLCanvasElement>;
  pdfPinchGestureRef: React.MutableRefObject<PdfPinchGestureState>;
  isShapeDrawing: boolean;
  shapesDrawingTools: ShapesDrawingToolsProps;
  selectedCommentId: string | null;
  activePageNumber: number;
  documentHighlight: IAssetViewerStateContextValue['documentHighlight'];
  zoomFactor: number;
  onClearHighlightObjects: () => void;
  renderTextHighlights: (rects: any) => void;
  setDocumentHighlight: IAssetViewerStateContextValue['setDocumentHighlight'];
  setDrawToolsShape: IAssetViewerStateContextValue['setDrawToolsShape'];
  setIsCommentFieldFocused: IAssetViewerStateContextValue['setIsCommentFieldFocused'];
};

export function useMobilePdfTextHighlight({
  pageRefsMap,
  pageCanvasRef,
  pdfPinchGestureRef,
  isShapeDrawing,
  shapesDrawingTools,
  selectedCommentId,
  activePageNumber,
  documentHighlight,
  zoomFactor,
  onClearHighlightObjects,
  renderTextHighlights,
  setDocumentHighlight,
  setDrawToolsShape,
  setIsCommentFieldFocused
}: UseMobilePdfTextHighlightParams) {
  const [selectedTextLength, setSelectedTextLength] = useState(0);
  const documentHighlightRef = useRef(documentHighlight);
  const textLayersMapRef = useRef<Map<HTMLDivElement, HTMLDivElement>>(
    new Map()
  );

  useEffect(() => {
    if (!selectedCommentId) return;
    if (
      documentHighlightRef.current?.rects?.length &&
      !documentHighlight.rects?.length
    ) {
      onClearHighlightObjects();
    }
  }, [selectedCommentId, documentHighlight.rects, onClearHighlightObjects]);

  useEffect(() => {
    if (!isShapeDrawing) return;
    if (shapesDrawingTools.shape !== 'documentHighlight') return;
    const pageCanvas = pageCanvasRef.current;
    let prevRange: Range | null = null;

    const onSelectionchange = () => {
      if (pdfPinchGestureRef.current.isActive) return;

      textLayersMapRef.current.clear();
      pageRefsMap.current.forEach((pageRoot) => {
        const textLayerDiv = pageRoot?.querySelector('.textLayer');
        const endDiv = pageRoot?.querySelector('.endOfContent');
        if (!textLayerDiv || !endDiv) return;
        textLayersMapRef.current.set(
          textLayerDiv as HTMLDivElement,
          endDiv as HTMLDivElement
        );
      });

      const range = handleSelectionChange(textLayersMapRef.current, prevRange);
      prevRange = range;

      if (!range) {
        const stagedHighlight = documentHighlightRef.current;
        if (!stagedHighlight?.rects?.length) {
          onClearHighlightObjects();
          setSelectedTextLength(0);
          return;
        }
        renderTextHighlights(stagedHighlight.rects);
        return;
      }

      const pageRoot = pageRefsMap.current.get(activePageNumber);
      const pageCoordsElement =
        pageRoot?.querySelector('.media_viewer_pdf-inner') ?? pageCanvas;

      if (!pageCoordsElement) {
        onClearHighlightObjects();
        setSelectedTextLength(0);
        return;
      }

      const rects = Array.from(range.getClientRects()).filter(
        (r) => r.width > 0 && r.height > 0
      );
      if (!rects.length) {
        onClearHighlightObjects();
        setSelectedTextLength(0);
        return;
      }
      const canvasRect = pageCoordsElement.getBoundingClientRect();
      const selectedCoords = rects
        .map((rect) => ({
          left: ((rect.left - canvasRect.left) / canvasRect.width) * 100,
          top: ((rect.top - canvasRect.top) / canvasRect.height) * 100,
          width: (rect.width / canvasRect.width) * 100,
          height: (rect.height / canvasRect.height) * 100
        }))
        .filter((r) => r.width > 0 && r.height > 0);

      if (!selectedCoords.length) {
        onClearHighlightObjects();
        setSelectedTextLength(0);
        return;
      }

      const fragment = range.cloneContents();
      documentHighlightRef.current = {
        text: Array.from(fragment.childNodes)
          .map(extractTextFromNode)
          .join('')
          .trim(),
        rects: selectedCoords
      };
      setSelectedTextLength(documentHighlightRef.current.text?.length || 0);
      renderTextHighlights(selectedCoords);
    };

    document.addEventListener('selectionchange', onSelectionchange);
    const textLayersMap = textLayersMapRef.current;
    return () => {
      prevRange = null;
      textLayersMap.clear();
      document.removeEventListener('selectionchange', onSelectionchange);
    };
  }, [
    selectedCommentId,
    shapesDrawingTools,
    isShapeDrawing,
    activePageNumber,
    pageRefsMap,
    pageCanvasRef,
    pdfPinchGestureRef,
    onClearHighlightObjects,
    renderTextHighlights
  ]);

  useEffect(() => {
    if (shapesDrawingTools.shape !== 'documentHighlight') return;
    if (!documentHighlightRef.current?.rects?.length) return;
    renderTextHighlights(documentHighlightRef.current.rects);
  }, [zoomFactor, shapesDrawingTools.shape, renderTextHighlights]);

  const onCancelTextHighlight = useCallback(() => {
    document.getSelection()?.removeAllRanges();
    setSelectedTextLength(0);
    onClearHighlightObjects();
    setDocumentHighlight(null);
    setDrawToolsShape('cursor');
  }, [onClearHighlightObjects, setDocumentHighlight, setDrawToolsShape]);

  const onConfirmTextHighlight = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const highlight = documentHighlightRef.current;
      if (!highlight?.rects?.length) return;

      setDocumentHighlight({
        text: highlight.text ?? '',
        rects: highlight.rects
      });
      setSelectedTextLength(0);
      setDrawToolsShape('cursor');
      setIsCommentFieldFocused(true);
      document.getSelection()?.removeAllRanges();
    },
    [setDocumentHighlight, setDrawToolsShape, setIsCommentFieldFocused]
  );

  return {
    selectedTextLength,
    onCancelTextHighlight,
    onConfirmTextHighlight
  };
}
